// src/context/AdminAuthContext.tsx
import React, { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { adminAuthService } from "../services/admin.auth.service";
import { adminTokenService, AdminData } from "../services/admin.token.service";

// Types for the context
interface AdminAuthContextType {
  adminData: AdminData | null;
  loading: boolean;
  loginAdmin: (email: string, password: string) => Promise<boolean>;
  logoutAdmin: () => Promise<void>;
}

// Create the context
const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

// Provider for the context
interface AdminAuthProviderProps {
  children: ReactNode;
}

export const AdminAuthProvider: React.FC<AdminAuthProviderProps> = ({ children }) => {
  const [adminData, setAdminData] = useState<AdminData | null>(null);
  const [loading, setLoading] = useState(true);

  // Effect to initialize auth state from token
  useEffect(() => {
    setLoading(true);
    const tokenData = adminTokenService.getAdminDataToken();
    if (tokenData && tokenData.adminData) {
      setAdminData(tokenData.adminData);
    }
    setLoading(false);
  }, []);

  // Login function
  const loginAdmin = async (email: string, password: string): Promise<boolean> => {
    try {
      const response = await adminAuthService.loginAdminWithEmail(email, password);
      if (response.success && response.adminData) {
        setAdminData(response.adminData);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error en login de admin:", error);
      return false;
    }
  };

  // Logout function
  const logoutAdmin = async (): Promise<void> => {
    try {
      await adminAuthService.logoutAdmin();
      setAdminData(null);
    } catch (error) {
      console.error("Error en logout de admin:", error);
    }
  };

  const value: AdminAuthContextType = {
    adminData,
    loading,
    loginAdmin,
    logoutAdmin,
  };

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
};

// Custom hook to use the admin auth context
export const useAdminAuth = (): AdminAuthContextType => {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error("useAdminAuth debe ser usado dentro de un AdminAuthProvider");
  }
  return context;
};
